import { ExpandMore, Groups } from '@mui/icons-material';
import {
  Box,
  List,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Show, useIsMobile } from '@side/pantry';
import { useAtom, useAtomValue } from 'jotai';
import type { Dictionary } from 'lodash';
import {
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtual } from 'react-virtual';
import {
  anchoredPartnerCardId,
  isAnyTeamDetailVisibleFamily,
  selectedGeocodeLocation,
} from '@/common/atoms/community';
import { Spinner } from '@/components/Spinner';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { DEFAULT_LOCALE_IDS } from '../../constants';
import type { TeamSearchResultVirtualItem } from '../../hooks/useGetPartners/helpers';
import { useIsVectorMap } from '../../hooks/useIsVectorMap';
import type { TeamSearchResult } from '../../types';
import { matches } from '../../utils/location';
import { EmptyListState } from '../EmptyListState';
import { PartnerCard } from '../PartnerCard';

const NEARBY_TEAMS_TOGGLE_HEIGHT = 60;
const NEARBY_TEAMS_TOGGLE_ID = 'NEARBY_TEAMS_TOGGLE';
const PARTNER_CARD_HEIGHT_ESTIMATE = 125;

const Accordion = styled(MuiAccordion)({
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
});

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  alignItems: 'center',
  '& .MuiAccordionSummary-content': {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    alignItems: 'center',
  },
  minHeight: NEARBY_TEAMS_TOGGLE_HEIGHT,
  strong: {
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  '&.Mui-expanded': {
    minHeight: NEARBY_TEAMS_TOGGLE_HEIGHT,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)({
  padding: 0,
});

interface Props {
  loading: boolean;
  partnerGroups: Dictionary<TeamSearchResult[]>;
}

const isVirtualTeamItem = (obj: {
  id: string;
}): obj is TeamSearchResultVirtualItem => obj.id !== NEARBY_TEAMS_TOGGLE_ID;

export const PartnerCardList = ({ partnerGroups, loading }: Props) => {
  const ref = useRef(null);
  const trackEvent = useTrackCommunityAnalytics();
  const isMobile = useIsMobile('md');
  const selectedLocation = useAtomValue(selectedGeocodeLocation);
  const [showNearbyPartners, setShowNearbyPartners] = useState(false);
  const isVectorMap = useIsVectorMap();
  const { focusedPartners, nearbyPartners } = useMemo(() => {
    if (
      !isVectorMap ||
      !selectedLocation ||
      DEFAULT_LOCALE_IDS.includes(selectedLocation.id)
    ) {
      return {
        focusedPartners: partnerGroups,
        nearbyPartners: {},
      };
    }

    const focused: typeof partnerGroups = {};
    const nearby: typeof partnerGroups = {};

    for (const [groupKey, partners] of Object.entries(partnerGroups)) {
      if (partners.length) {
        if (matches(selectedLocation, partners[0].mailingAddress)) {
          focused[groupKey] = partners;
        } else {
          nearby[groupKey] = partners;
        }
      }
    }

    return {
      focusedPartners: focused,
      nearbyPartners: nearby,
    };
  }, [selectedLocation, partnerGroups, isVectorMap]);

  // Track analytics in a separate effect
  useEffect(() => {
    if (
      isVectorMap &&
      selectedLocation &&
      !DEFAULT_LOCALE_IDS.includes(selectedLocation.id)
    ) {
      trackEvent(TrackCommunityEvent.PartnerMapDataFetch, {
        numTeams: [
          Object.values(focusedPartners).reduce(
            (count, partners) => count + partners.length,
            0,
          ),
          Object.values(nearbyPartners).reduce(
            (count, partners) => count + partners.length,
            0,
          ),
        ],
      });
    }
  }, [
    focusedPartners,
    nearbyPartners,
    isVectorMap,
    selectedLocation,
    trackEvent,
  ]);

  const nearbyPartnerIds = useMemo(
    () =>
      Object.values(nearbyPartners).flatMap((partnerArray) =>
        partnerArray.map(({ id }) => id),
      ),
    [nearbyPartners],
  );
  const nearbyPartnerSelected = useAtomValue(
    isAnyTeamDetailVisibleFamily(nearbyPartnerIds),
  );

  // Auto-expand nearby partners when nearby partner is selected
  useEffect(() => {
    if (nearbyPartnerSelected) {
      setShowNearbyPartners(true);
    }
  }, [nearbyPartnerSelected]);

  const numNearbyPartners = Object.values(nearbyPartners).reduce(
    (count, partners) => count + partners.length,
    0,
  );

  const listItems = useMemo(() => {
    const focused = Object.entries(focusedPartners).flatMap(([key, val]) =>
      val.map((item, itemIndex) => ({
        groupKey: key,
        ...item,
        isFocused: true,
        isFirstInGroup: itemIndex === 0,
        isLastInGroup: itemIndex === val.length - 1,
      })),
    );
    const nearby = Object.entries(nearbyPartners).flatMap(([key, val]) =>
      val.map((item, itemIndex) => ({
        groupKey: key,
        ...item,
        isFocused: false,
        isFirstInGroup: itemIndex === 0,
        isLastInGroup: itemIndex === val.length - 1,
      })),
    );
    return [
      focused,
      ...(nearby.length > 0
        ? [{ id: NEARBY_TEAMS_TOGGLE_ID, groupKey: 'none', isFocused: false }]
        : []),
      nearby,
    ].flat();
  }, [focusedPartners, nearbyPartners]);

  const rowVirtualizer = useVirtual({
    size: listItems.length,
    parentRef: ref,
    estimateSize: useCallback(
      (index: number) => {
        const item = listItems[index];

        if (item.id === NEARBY_TEAMS_TOGGLE_ID) {
          return NEARBY_TEAMS_TOGGLE_HEIGHT;
        }

        if (!item.isFocused && !showNearbyPartners) {
          return 0;
        }

        return PARTNER_CARD_HEIGHT_ESTIMATE;
      },
      [listItems, showNearbyPartners],
    ),
    keyExtractor: (index) => `partner-${index + 1}`,
  });

  const accordionItem = rowVirtualizer.virtualItems.find(
    ({ index }) => listItems[index].id === 'NEARBY_TEAMS_TOGGLE',
  );

  const [anchoredPartnerId, setAnchoredPartnerId] = useAtom(
    anchoredPartnerCardId,
  );

  // Scroll to "anchored" partner card, if any
  useEffect(() => {
    if (anchoredPartnerId) {
      const index = listItems.findIndex(({ id }) => id === anchoredPartnerId);
      if (index !== undefined) {
        rowVirtualizer.scrollToIndex(index, { align: 'start' });
      }
    }
  }, [anchoredPartnerId, rowVirtualizer, listItems]);

  // Reset anchored partner when user scrolls
  const handleScroll = (e: UIEvent<HTMLElement>) => {
    if (!anchoredPartnerId) return;

    const anchoredPartner = rowVirtualizer.virtualItems.find(
      (item) => listItems[item.index].id === anchoredPartnerId,
    );

    if (
      anchoredPartner &&
      (e.target as HTMLElement).scrollTop !== anchoredPartner.start
    ) {
      setAnchoredPartnerId(undefined);
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'common.white',
        border: isMobile
          ? 'none'
          : (theme) => `1px solid ${theme.palette.grey[300]}`,
        flex: '0 0 400px',
        position: 'relative',
      }}
      className={loading ? 'loading' : undefined}
    >
      <Show
        when={!loading && !isMobile && !Object.entries(focusedPartners).length}
      >
        <EmptyListState
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      </Show>
      <Box
        ref={ref}
        sx={{
          height: '100%',
          overflowY: 'auto',
        }}
        onScroll={handleScroll}
      >
        <div
          style={{
            position: 'relative',
            height: `${rowVirtualizer.totalSize}px`,
            overflow: 'hidden',
          }}
        >
          <Show when={Object.entries(focusedPartners).length > 0}>
            <List sx={{ p: 0 }}>
              {rowVirtualizer.virtualItems.map(
                ({ start, index, key, measureRef }) => {
                  const item = listItems[index];
                  if (!item.isFocused || !isVirtualTeamItem(item)) return null;
                  return (
                    <li
                      key={key}
                      ref={measureRef}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        top: 0,
                        transform: `translateY(${start}px)`,
                      }}
                    >
                      <PartnerCard {...item} index={index} />
                    </li>
                  );
                },
              )}
            </List>
          </Show>
          <Show when={numNearbyPartners > 0}>
            <Accordion
              expanded={showNearbyPartners}
              onChange={(_e, isExpanded) => {
                setShowNearbyPartners(isExpanded);
                trackEvent(TrackCommunityEvent.PartnerMapNearbyTeamsClick, {
                  task: isExpanded ? 'expand' : 'collapse',
                });
              }}
            >
              {!!accordionItem && (
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: accordionItem.size,
                    top: 0,
                    transform: `translateY(${accordionItem.start}px)`,
                  }}
                >
                  <Groups fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">
                    {showNearbyPartners ? 'Hide' : 'Show'}{' '}
                    <strong>
                      {numNearbyPartners}{' '}
                      {`team${numNearbyPartners > 1 ? 's' : ''}`}
                    </strong>{' '}
                    nearby
                  </Typography>
                </AccordionSummary>
              )}
              <AccordionDetails>
                <List sx={{ p: 0 }}>
                  {rowVirtualizer.virtualItems.map(
                    ({ start, index, key, measureRef }) => {
                      const item = listItems[index];
                      if (item.isFocused || !isVirtualTeamItem(item))
                        return null;
                      return (
                        <li
                          key={key}
                          ref={measureRef}
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: showNearbyPartners ? 'auto' : 0,
                            top: 0,
                            transform: `translateY(${start}px)`,
                          }}
                        >
                          <PartnerCard {...item} index={index} />
                        </li>
                      );
                    },
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Show>
        </div>
      </Box>
      {loading && (
        <Spinner
          centered
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            pointerEvents: 'none',
          }}
        />
      )}
    </Stack>
  );
};
